import React from 'react';
import { Container, Box, Typography, Paper } from '@mui/material';
import SectionHeader from './SectionHeader';

const PricingComparison = () => {
  const pricingData = [
    {
      solution: "Sashy",
      description: "Multi-platform reviews + sales data",
      pricing: [12, 29, 89, 299], // Updated: Solo, Starter, Growth, Professional
      color: '#1976d2',
      highlight: true
    },
    {
      solution: "Enterprise CX Platforms",
      description: "Software-only, with volume discounts",
      pricing: [89, 267, 400, 2500], // $89/location, then $89, $40, $25 per location
      color: '#f57c00',
      highlight: false
    },
    {
      solution: "Traditional CX Consultants",
      description: "Manual analysis + reports",
      pricing: [1500, 4000, 12000, 30000], // Updated more realistic pricing
      color: '#d32f2f',
      highlight: false
    }
  ];

  const chartMaxPrice = 5000; // Fixed max at $5k

  const locations = ["1 Location", "Up to 3 Locations", "Up to 10 Locations", "Up to 100 Locations"]; // Updated location tiers

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <SectionHeader 
          title="10x Cheaper Than Existing Solutions"
          subtitle="Pay monthly, cancel anytime - no contracts or commitments required"
        />
        
        {/* Chart */}
        <Paper elevation={0} sx={{ 
          p: { xs: 0.5, md: 1.5 }, 
          borderRadius: '16px', 
          border: '1px solid rgba(25, 118, 210, 0.1)',
          backgroundColor: '#fafafa'
        }}>
          <Box sx={{ mt: { xs: 1.5, md: 2 }, mb: { xs: 0.5, md: 1 } }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: '#0A2540', 
              mb: 0.5,
              textAlign: 'center'
            }}>
              Total Monthly Cost Comparison
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#546E7A', 
              textAlign: 'center'
            }}>
              Total cost for all locations - see the massive savings with Sashy
            </Typography>
          </Box>

          {/* Line Chart Container */}
          <Box sx={{ 
            height: { xs: 300, md: 400 },
            position: 'relative',
            p: { xs: 0.5, md: 3 },
            overflowX: { xs: 'auto', md: 'visible' },
            overflowY: 'visible'
          }}>
            {/* SVG Line Chart */}
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 800 300"
              style={{ 
                overflow: 'visible',
                minWidth: '600px' // Ensure minimum width on mobile
              }}
            >
              {/* Grid lines and Y-axis labels */}
              {[0, 1000, 2000, 3000, 4000, 5000].map((value, index) => {
                const y = 220 - (value / chartMaxPrice) * 180;
                return (
                  <g key={index}>
                    <line
                      x1="60"
                      y1={y}
                      x2="760"
                      y2={y}
                      stroke="#e0e0e0"
                      strokeWidth="1"
                      strokeDasharray="2,2"
                    />
                    <text
                      x="50"
                      y={y + 5}
                      textAnchor="end"
                      fontSize="12"
                      fill="#666"
                    >
                      ${value === 0 ? '0' : `${(value / 1000)}k`}
                    </text>
                  </g>
                );
              })}
              
              {/* X-axis labels */}
              {locations.map((location, index) => {
                const positions = [80, 293, 506, 720];
                const x = positions[index];
                return (
                  <text
                    key={index}
                    x={x}
                    y="260"
                    textAnchor="middle"
                    fontSize="12"
                    fill="#666"
                  >
                    {location}
                  </text>
                );
              })}
              
                            {/* Lines and data points */}
              {pricingData.map((solution, solutionIndex) => {
                // Build path with clipping at chart max
                let pathSegments = [];
                let visiblePoints = [];
                
                for (let index = 0; index < solution.pricing.length; index++) {
                  const price = solution.pricing[index];
                  const positions = [80, 293, 506, 720];
                  const x = positions[index];
                  let y = 220 - (price / chartMaxPrice) * 180;
                  
                                    // If price exceeds chart max, clip at boundary
                  if (price > chartMaxPrice) {
                    y = 40; // Top of chart
                    
                    // Calculate intersection point if previous point was within bounds
                    if (index > 0 && solution.pricing[index - 1] <= chartMaxPrice) {
                      const prevPrice = solution.pricing[index - 1];
                      const positions = [80, 293, 506, 720];
                      const prevX = positions[index - 1];
                      const prevY = 220 - (prevPrice / chartMaxPrice) * 180;
                      
                      // Linear interpolation to find exact intersection
                      const priceDiff = price - prevPrice;
                      const exceedAmount = price - chartMaxPrice;
                      const ratio = (priceDiff - exceedAmount) / priceDiff;
                      const intersectX = prevX + (x - prevX) * ratio;
                      const intersectY = 40;
                      
                      pathSegments.push(`L ${intersectX} ${intersectY}`);
                      break; // Stop the line here
                    }
                  } else {
                    visiblePoints.push({ x, y, price, index });
                    pathSegments.push(`${index === 0 ? 'M' : 'L'} ${x} ${y}`);
                  }
                }
                
                const pathData = pathSegments.join(' ');
                
                return (
                  <g key={solutionIndex}>
                    {/* Line */}
                    <path
                      d={pathData}
                      stroke={solution.color}
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    
                    {/* Data points - only for visible points */}
                    {visiblePoints.map((point, pointIndex) => (
                      <g key={pointIndex}>
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r="6"
                          fill={solution.color}
                          stroke="white"
                          strokeWidth="2"
                        />
                        {/* Price label */}
                        <text
                          x={point.x}
                          y={(() => {
                            // For Sashy, put first three price labels below the points (including $89)
                            if (solution.solution === "Sashy" && point.index <= 2) {
                              return point.y + 22; // Below the point, raised up slightly
                            }
                            // For Traditional, raise up point 2 (index 1)
                            if (solution.solution === "Traditional CX Consultants" && point.index === 1) {
                              return point.y - 25; // Raised higher than default
                            }
                            // Move the 299 label down slightly
                            if (point.price === 299) {
                              return point.y - 10; // Lower position for 299
                            }
                            return point.y - 15; // Above the point (default)
                          })()}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="600"
                          fill={solution.color}
                        >
                          ${point.price >= 1000 ? `${(Math.round(point.price/1000))}k` : Math.round(point.price).toLocaleString()}
                        </text>
                      </g>
                    ))}
                    
                    {/* Series name label above the line */}
                    {visiblePoints.length > 0 && (
                      <text
                        x={(() => {
                          // For Sashy and Enterprise, position at midpoint between final and second-to-last points
                          if (solution.solution === "Sashy" || solution.solution === "Enterprise CX Platforms") {
                            if (visiblePoints.length >= 2) {
                              const lastPoint = visiblePoints[visiblePoints.length - 1];
                              const secondLastPoint = visiblePoints[visiblePoints.length - 2];
                              return (lastPoint.x + secondLastPoint.x) / 2;
                            }
                            return visiblePoints[0].x;
                          }
                          // For Traditional, position at midpoint between first and second points, shifted left
                          if (visiblePoints.length >= 2) {
                            const firstPoint = visiblePoints[0];
                            const secondPoint = visiblePoints[1];
                            return (firstPoint.x + secondPoint.x) / 2 - 10; // Shift left by 10 pixels
                          }
                          return visiblePoints[0].x;
                        })()}
                        y={(() => {
                          // Position all labels above their lines at the same distance
                          if (solution.solution === "Sashy") {
                            if (visiblePoints.length >= 2) {
                              const lastPoint = visiblePoints[visiblePoints.length - 1];
                              const secondLastPoint = visiblePoints[visiblePoints.length - 2];
                              return (lastPoint.y + secondLastPoint.y) / 2 - 15; // Above the line
                            }
                            return visiblePoints[0].y - 25;
                          }
                          if (solution.solution === "Enterprise CX Platforms") {
                            if (visiblePoints.length >= 2) {
                              const lastPoint = visiblePoints[visiblePoints.length - 1];
                              const secondLastPoint = visiblePoints[visiblePoints.length - 2];
                              return (lastPoint.y + secondLastPoint.y) / 2 - 15; // Above the line
                            }
                            return visiblePoints[0].y - 25;
                          }
                          // For Traditional, position at midpoint between first and second points, above the line
                          if (visiblePoints.length >= 2) {
                            const firstPoint = visiblePoints[0];
                            const secondPoint = visiblePoints[1];
                            return (firstPoint.y + secondPoint.y) / 2 - 15; // Above the line
                          }
                          return visiblePoints[0].y - 25;
                        })()}
                        textAnchor="middle"
                        fontSize="12"
                        fontWeight="600"
                        fill={solution.color}
                        style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.8)' }}
                        transform={(() => {
                          // Calculate rotation angle to match line gradient
                          let angle = 0;
                          
                                                    if (solution.solution === "Sashy") {
                            if (visiblePoints.length >= 2) {
                              const lastPoint = visiblePoints[visiblePoints.length - 1];
                              const secondLastPoint = visiblePoints[visiblePoints.length - 2];
                              const deltaX = lastPoint.x - secondLastPoint.x;
                              const deltaY = lastPoint.y - secondLastPoint.y;
                              angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
                              
                              const centerX = (lastPoint.x + secondLastPoint.x) / 2;
                              const centerY = (lastPoint.y + secondLastPoint.y) / 2 - 25;
                              return `rotate(${angle}, ${centerX}, ${centerY})`;
                            }
                          } else if (solution.solution === "Enterprise CX Platforms") {
                            if (visiblePoints.length >= 2) {
                              const lastPoint = visiblePoints[visiblePoints.length - 1];
                              const secondLastPoint = visiblePoints[visiblePoints.length - 2];
                              const deltaX = lastPoint.x - secondLastPoint.x;
                              const deltaY = lastPoint.y - secondLastPoint.y;
                              angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
                              
                              const centerX = (lastPoint.x + secondLastPoint.x) / 2;
                              const centerY = (lastPoint.y + secondLastPoint.y) / 2 - 25;
                              return `rotate(${angle}, ${centerX}, ${centerY})`;
                            }
                          } else {
                                                         // For Traditional, use first two points
                            if (visiblePoints.length >= 2) {
                              const firstPoint = visiblePoints[0];
                              const secondPoint = visiblePoints[1];
                              const deltaX = secondPoint.x - firstPoint.x;
                              const deltaY = secondPoint.y - firstPoint.y;
                              angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
                              
                              const centerX = (firstPoint.x + secondPoint.x) / 2 - 10; // Match the shifted left position
                              const centerY = (firstPoint.y + secondPoint.y) / 2 - 25; // Match the moved up position
                              return `rotate(${angle}, ${centerX}, ${centerY})`;
                            }
                          }
                          
                          return '';
                        })()}
                      >
                        {solution.solution}
                      </text>
                    )}
                    
                    {/* Add indication when line is cut off */}
                    {solution.pricing.some(price => price > chartMaxPrice) && (
                      <text
                        x={760}
                        y={30}
                        textAnchor="end"
                        fontSize="10"
                        fill={solution.color}
                        opacity="0.8"
                        fontStyle="italic"
                      >
                        {solution.solution} continues beyond chart...
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </Box>


        </Paper>

        {/* Comparison Table */}
        <Box sx={{ mt: 6, overflowX: 'auto' }}>
          <Paper elevation={0} sx={{ 
            borderRadius: '16px', 
            border: '1px solid rgba(25, 118, 210, 0.1)',
            minWidth: '800px'
          }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', 
              gap: 0 
            }}>
              {/* Header Row */}
              <Box sx={{ 
                p: 3, 
                backgroundColor: '#f8f9fe', 
                borderRadius: '16px 0 0 0', 
                borderBottom: '1px solid rgba(25, 118, 210, 0.1)' 
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2540' }}>
                  Solution
                </Typography>
              </Box>
              {locations.map((location, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    p: 3, 
                    backgroundColor: '#f8f9fe', 
                    borderBottom: '1px solid rgba(25, 118, 210, 0.1)', 
                    borderLeft: '1px solid rgba(25, 118, 210, 0.1)',
                    textAlign: 'center',
                    ...(index === locations.length - 1 && {
                      borderRadius: '0 16px 0 0'
                    })
                  }}
                >
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    color: '#0A2540'
                  }}>
                    {location}
                  </Typography>
                </Box>
              ))}
              
              {/* Data Rows */}
              {pricingData.map((row, rowIndex) => {
                const locationCounts = [1, 3, 10, 100]; // Updated location counts
                return (
                  <React.Fragment key={rowIndex}>
                    <Box sx={{ 
                      p: 3, 
                      backgroundColor: row.highlight ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                      borderRight: '1px solid rgba(25, 118, 210, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      ...(rowIndex === pricingData.length - 1 && {
                        borderRadius: '0 0 0 16px'
                      })
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          width: 16, 
                          height: 16, 
                          backgroundColor: row.color,
                          borderRadius: '4px',
                          flexShrink: 0
                        }} />
                        <Box>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700, 
                            color: row.highlight ? '#1976d2' : '#0A2540',
                            mb: 0.5,
                            fontSize: '1rem'
                          }}>
                            {row.solution}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#546E7A', fontSize: '0.85rem' }}>
                            {row.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {row.pricing.map((totalPrice, priceIndex) => {
                      const locationCount = locationCounts[priceIndex];
                      const perLocationPrice = totalPrice / locationCount;
                      return (
                        <Box 
                          key={priceIndex}
                          sx={{ 
                            p: 3, 
                            backgroundColor: row.highlight ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                            borderLeft: '1px solid rgba(25, 118, 210, 0.1)', 
                            textAlign: 'center',
                            ...(rowIndex === pricingData.length - 1 && priceIndex === row.pricing.length - 1 && {
                              borderRadius: '0 0 16px 0'
                            })
                          }}
                        >
                          {/* Total Price */}
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700, 
                            color: row.highlight ? '#1976d2' : '#d32f2f',
                            mb: 0.5
                          }}>
                            ${totalPrice >= 1000 ? `${Math.round(totalPrice/1000)}k` : Math.round(totalPrice).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#546E7A', mb: 1 }}>
                            total monthly
                          </Typography>
                          {/* Per Location Price */}
                                                     <Typography variant="body2" sx={{ 
                             fontWeight: 600, 
                             color: '#546E7A',
                             fontSize: '0.85rem'
                           }}>
                             ${perLocationPrice >= 1000 ? `${(perLocationPrice/1000).toFixed(1)}k` : perLocationPrice.toFixed(2)} per location
                           </Typography>
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Box>
          </Paper>
        </Box>
        

      </Container>
    </Box>
  );
};

export default PricingComparison; 